<template>
  <modal
    header
    wide
    tall
    data-test="sportradar-changeDataModal"
    @close="$emit('close')"
  >
    <template #modal-header>
      <button
        class="modal-close"
        data-test="sportradar-changeDataModal-close"
        @click="$emit('close')"
      >
        <span class="fal fa-times" />
      </button>

      <h4>Change Data</h4>
    </template>

    <template #modal-body>
      <div class="sr-profile">
        <div class="sr-profile__content">
          <div class="sr-profile__left sportradar-mapper">
            <div class="sportradar-mapper__scrollable">
              <p class="font-weight-bold">
                Data You Wish to Change
              </p>
              <div class="sr-profile__selected-data">
                <div>
                  <p class="sr-profile__selected-data-key">
                    {{ $filters.snakeToCapitalized(selectedData.key) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="sr-profile__right sportradar-mapper">
            <p class="sr-profile__small-title margin-top-lg">
              Switch out your current data with a new data point.
            </p>
            <div class="google-connect-form__search margin-bottom-md">
              <input
                v-model="searchTerm"
                type="text"
                class="mx-input"
                placeholder="Search..."
                data-test="graphics-sportradarChangeDataModal-search"
              >
              <span class="fas fa-search" />
            </div>
            <div class="btn-group">
              <button
                :class="['btn btn-sm btn-style-b', showAllData ? 'selected' : '']"
                :aria-pressed="showAllData"
                data-test="graphics-sportradarChangeDataModal-showAllData"
                data-entity="graphics-sportradarChangeDataModal-showAllData"
                @click="showAllData = true"
              >
                Show All Data
              </button>
              <button
                :class="['btn btn-sm btn-style-b', !showAllData ? 'selected' : '']"
                :aria-pressed="!showAllData"
                data-test="graphics-sportradarChangeDataModal-showSelectedData"
                data-entity="graphics-sportradarChangeDataModal-showSelectedData"
                @click="showAllData = false"
              >
                Show Selected Data
              </button>
            </div>
            <div class="sportradar-mapper__scrollable margin-top-lg">
              <div
                v-if="!formattedData || !formattedData.length"
                class="text-center padding-md font-weight-bold"
              >
                Data Not Found
                <hr />
                Check data providers and ensure data is synced and available
              </div>
              <key-mapper
                v-else
                v-model="selectedKey"
                :data="formattedData"
                :force-expand="!!searchTerm"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
import { mapState } from 'vuex';
import { pick } from 'lodash-es';
import Fuse from 'fuse.js';
import sportradarMixin from '../../../../mixins/sportradar-mixin';
import leagueInfo from '../../../../utils/leagueInfo';

import Modal from '../../../Modal/Modal.vue';
import KeyMapper from './KeyMapper.vue';

import flatten from 'flat';

const { unflatten } = flatten;

export default {
  emits: ['close', 'update-map'],
  name: 'SportradarChangeData',
  components: {
    Modal,
    KeyMapper,
  },
  mixins: [sportradarMixin],
  props: {
    dataProvider: {
      required: true,
    },
    element: {
      required: true,
    },
    property: {
      required: true,
    },
  },
  data() {
    return {
      searchTerm: null,
      showAllData: false,
    };
  },
  computed: {
    ...mapState({
      translationKeys(state, getters) {
        return getters[`${this.dataProvider.id}/translationKeys`];
      },
      abstractionKeys(state, getters) {
        return getters[`${this.dataProvider.id}/abstractionKeys`];
      },
      dataFilters(state, getters) {
        return getters[`${this.dataProvider.id}/dataFilters`];
      },
      playerSeasonStatsSchema(state) {
        const { leagueName } = state[this.dataProvider.id];
        if (leagueInfo[leagueName]?.statsProvider === 'sportsstack') {
          return state[this.dataProvider.id].schemaInfo[`PlayerStatistics${leagueName.toUpperCase()}`];
        }
        return {
          total: state[this.dataProvider.id].schemaInfo[`PlayerSeasonStatsTotal${leagueName.toUpperCase()}`],
          average: state[this.dataProvider.id].schemaInfo[`PlayerSeasonStatsAverage${leagueName.toUpperCase()}`],
        };
      },
      teamSeasonStatsSchema(state) {
        const { leagueName } = state[this.dataProvider.id];
        if (leagueInfo[leagueName]?.statsProvider === 'sportsstack') {
          return state[this.dataProvider.id].schemaInfo[`TeamStatistics${leagueName.toUpperCase()}`];
        }
        const statsTotalSchemaState = state[this.dataProvider.id].schemaInfo[`TeamSeasonStatsTotal${leagueName.toUpperCase()}`];
        const statsAverageSchemaState = state[this.dataProvider.id].schemaInfo[`TeamSeasonStatsAverage${leagueName.toUpperCase()}`];
        return {
          opponentsTotal: statsTotalSchemaState,
          opponentsAverage: statsAverageSchemaState,
          teamTotal: statsTotalSchemaState,
          teamAverage: statsAverageSchemaState,
        };
      },
      teamGameStatsSchema(state) {
        const { leagueName } = state[this.dataProvider.id];
        const sportsStackSchema = state[this.dataProvider.id].schemaInfo[`TeamStatistics${leagueName.toUpperCase()}`];
        const sportRadarSchema = state[this.dataProvider.id].schemaInfo[`TeamGameStatistics${leagueName.toUpperCase()}`];
        return { statistics: sportsStackSchema || sportRadarSchema };
      },
      playerGameStatsSchema(state) {
        const { leagueName } = state[this.dataProvider.id];
        const sportsStackSchema = state[this.dataProvider.id].schemaInfo[`PlayerStatistics${leagueName.toUpperCase()}`];
        const sportRadarSchema = state[this.dataProvider.id].schemaInfo[`PlayerGameStats${leagueName.toUpperCase()}`];
        return { statistics: sportsStackSchema || sportRadarSchema };
      },
    }),
    propertyData() {
    // Fetch data using the getter once to avoid unnecessary reactivity
      return this.getDataForProperty(this.property);
    },
    relevantStatData() {
      const statTypeToSchema = {
        TeamGameStats: this.teamGameStatsSchema,
        TeamSeasonStats: this.teamSeasonStatsSchema,
        PlayerGameStats: this.playerGameStatsSchema,
        PlayerSeasonStats: this.playerSeasonStatsSchema,
      };

      const statType = Object.keys(statTypeToSchema).find((type) => this.propertyData[type]);

      return statType
        ? { [statType]: { ...statTypeToSchema[statType] } }
        : {};
    },
    relevantData() {
      return {
        ...this.propertyData,
        ...this.relevantStatData,
      };
    },
    searchedData() {
      let finalObj = this.relevantData;

      if (this.searchTerm) {
        const flatData = flatten(finalObj);
        const keys = new Fuse(Object.keys(flatData), {
          threshold: 0.5,
          ignoreLocation: true,
        })
          .search(this.searchTerm)
          .map((r) => r.item);
        const searchedObj = pick(flatData, keys);
        finalObj = unflatten(searchedObj);
      }

      if (!this.showAllData) {
        const nearby = this.selectedKey.split('.');
        nearby.pop(); // remove last key
        return pick(finalObj, nearby.join('.'));
      }

      return finalObj;
    },
    formattedData() {
      return this.formatForKeyMapper(
        this.searchedData,
        this.relevantData,
        this.dataFilters,
      );
    },
    selectedKey: {
      get() {
        // TODO: use isMapped for this check?
        if (this.element.data?.dataKeyMap?.[this.property]) {
          return this.getTranslatedPath(
            this.element.data.dataKeyMap[this.property],
            this.translationKeys,
          );
        }

        return '';
      },
      set(v) {
        const abstracted = this.makeAbstractPath(v, this.abstractionKeys);
        this.mapField(abstracted);
      },
    },
    selectedData() {
      return {
        key: this.selectedKey.split('.').pop(),
      };
    },
  },
  methods: {
    getDataForProperty(property) {
      // Fetch data using the getter once to avoid unnecessary reactivity
      return this.$store.getters[`${this.dataProvider.id}/getDataForProperty`](property);
    },
    mapField(value) {
      if (!this.dataProvider) return;

      const data = {
        path: `data.dataKeyMap.${this.property}`,
        value,
        property: this.property,
      };

      this.$emit('update-map', data);
    },
  },
};
</script>
